import {AddIcon, MinusIcon} from 'native-base';
import {Dimensions, StyleSheet} from 'react-native';
import {H3Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useState} from 'react';
import {formatCurrency, isItemOrganic, isWeb} from '@buncha/utils/common';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {BottomButton} from '@buncha/components/composites/Button';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import Box from '@buncha/components/core/view/Box';
import {HBox} from '@buncha/components/core/view';
import If from '@buncha/components/conditional/If';
import ImageWithQuantityPill from '@buncha/common/components/ImageWithQuantityPill';
import ItemName from '@buncha/batchDetails/component/ItemName';
import ItemTypePill from '@buncha/batchDetails/component/ItemName/ItemTypePill';
import {Modules} from '@buncha/utils/types';
import {PTextInput} from '@buncha/components/core/input/TextInput';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import font from '@buncha/appStyles/style/font';
import {useGetLocale} from '@buncha/localization/hooks';

interface UpdateQuantitySheetProps {
  open: boolean;
  onClose: () => void;
  quantity: number;
  handleUpdateQuantity: (qty: number) => void;
  loading?: boolean;
  itemName?: string;
  itemImageUrl?: string;
  itemPrice?: number;
}

const isWebMode = isWeb();

const UpdateQuantitySheet: React.FC<UpdateQuantitySheetProps> = props => {
  const [updatedQty, setUpdatedQty] = useState(props?.quantity);

  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const mortiseFont = useFontTheme('mortise');
  const {title, body, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const quantityUpdate = useCallback((quantity: string) => {
    const qty = Number(quantity);
    if (qty < 100 && qty >= 0) setUpdatedQty(qty);
  }, []);

  const modifyQty = useCallback(
    (quantity: number) => {
      const val = quantity ? quantity.toString() : '';
      quantityUpdate(val);
    },
    [quantityUpdate],
  );

  const modifyAddQty = useCallback(() => {
    modifyQty(updatedQty + 1);
  }, [modifyQty, updatedQty]);

  const modifySubQty = useCallback(() => {
    modifyQty(updatedQty - 1);
  }, [modifyQty, updatedQty]);

  return (
    <BottomSheet open={props.open} onClose={props?.onClose}>
      <Box style={styles.boxContainer}>
        <H3Text
          style={[
            styles.text,
            {
              color: theme?.text.blue[300],
              fontFamily: mortiseFont[700].normal,
            },
          ]}>
          {title?.updateQuantity}
        </H3Text>
        <H6Text
          style={[
            styles.text,
            {
              color: theme?.text.gray[100],
              fontFamily: muliFont[600].normal,
            },
          ]}>
          {body?.adjustQuantity}
        </H6Text>
        <HBox style={styles.quantityBox}>
          <HBox style={styles.imageContainer}>
            <ImageWithQuantityPill imageLink={props?.itemImageUrl ?? ''} />
            <Box style={styles.textBox}>
              <If condition={isItemOrganic(props?.itemName ?? '')}>
                <ItemTypePill />
              </If>
              <ItemName name={props?.itemName ?? ''} nameStyle={styles.text} />
              <H6Text
                style={[
                  styles.text,
                  {
                    color: theme?.text.blue[300],
                    fontFamily: muliFont[600].normal,
                  },
                ]}>
                {formatCurrency(props?.itemPrice ?? 0)}
              </H6Text>
            </Box>
          </HBox>
          <Box
            style={[
              styles.quantityContainer,
              styles.quantityPill,
              {
                borderColor: theme?.background.blue[300],
              },
            ]}>
            <HBox style={styles.quantityContainer}>
              <Touchable
                activeOpacity={1}
                hitSlop={styles.hitSlopStyles}
                disabled={!updatedQty}
                onPress={modifySubQty}>
                <MinusIcon
                  size={3}
                  color={
                    !updatedQty
                      ? theme?.background.gray[100]
                      : theme?.background.blue[300]
                  }
                />
              </Touchable>
              <PTextInput
                placeholder="0"
                placeholderTextColor={theme?.text.gray[300]}
                debounce={false}
                textAlign="center"
                value={String(updatedQty) ?? ''}
                onChangeText={quantityUpdate}
                keyboardType="number-pad"
                returnKeyType="done"
                containerStyle={[styles.inputStyle]}
                style={{
                  fontFamily: muliFont[700].normal,
                  ...font.FontSize.fontH3,
                  color: theme?.text.blue[300],
                }}
              />
              <Touchable
                activeOpacity={1}
                hitSlop={styles.hitSlopStyles}
                style={styles.addIconContainer}
                disabled={updatedQty >= 99}
                onPress={modifyAddQty}>
                <AddIcon size={3} color={theme?.background.blue[300]} />
              </Touchable>
            </HBox>
          </Box>
        </HBox>
        <BottomButton
          style={[styles.buttonContainer]}
          title={button?.updateQuantity}
          onPress={() => props.handleUpdateQuantity(updatedQty)}
          disabled={
            props?.loading || updatedQty === props?.quantity || updatedQty <= 0
          }
          loadingText={button?.updating}
          isLoading={props?.loading}
        />
      </Box>
    </BottomSheet>
  );
};

export default React.memo(UpdateQuantitySheet);

const styles = StyleSheet.create({
  boxContainer: {
    width: '95%',
  },
  buttonContainer: {
    width: '100%',
    marginTop: 10,
  },
  text: {
    textAlign: 'left',
    marginVertical: 1,
  },
  hitSlopStyles: {
    top: 25,
    bottom: 25,
    left: 15,
    right: 15,
  },
  inputContainer: {
    borderWidth: 1,
    height: 51,
    width: '100%',
    borderRadius: 50,
    marginTop: 10,
  },
  quantityBox: {
    marginTop: 10,
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  textBox: {
    marginLeft: 10,
    gap: 2.5,
    flexShrink: 1,
    paddingRight: 5,
  },
  inputStyle: {
    width: isWebMode ? 50 : Dimensions.get('screen').width * 0.18,
    height: Dimensions.get('screen').height * 0.02,
  },
  quantityContainer: {
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  quantityPill: {
    borderWidth: 1,
    padding: 10,
    borderRadius: 50,
  },
  addIconContainer: {paddingVertical: 5},
});
